import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, ErrorApi, fijarAccessToken } from '../lib/api.ts';
import { CLAVE_SESION } from '../lib/sesion.ts';
import { Aviso, Boton, Campo } from '../componentes/ui.tsx';

interface RespuestaLogin {
  accessToken?: string;
  debeCambiarPassword?: boolean;
  elegirEmpresa?: { slug: string; nombre: string }[];
}

/**
 * Login con usuario + clave, SIN selector de empresa: un listado público revelaría la
 * cartera de clientes. Si las credenciales valen en más de una cuenta (D2), la API
 * devuelve las empresas de ESE usuario y se elige en un segundo paso.
 */
export function PaginaLogin() {
  const { t } = useTranslation();
  const navegar = useNavigate();
  const qc = useQueryClient();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [opciones, setOpciones] = useState<{ slug: string; nombre: string }[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function ingresar(empresaSlug?: string) {
    setError(null);
    setEnviando(true);
    try {
      const resp = await api<RespuestaLogin>('/auth/login', {
        metodo: 'POST',
        cuerpo: {
          username: username.trim(),
          password,
          plataforma: 'web',
          ...(empresaSlug ? { empresaSlug } : {}),
        },
      });

      if (resp.elegirEmpresa) {
        // Sólo las cuentas de este usuario, ya autenticado.
        setOpciones(resp.elegirEmpresa);
        return;
      }

      fijarAccessToken(resp.accessToken ?? null);
      await qc.invalidateQueries({ queryKey: CLAVE_SESION });
      await navegar({ to: resp.debeCambiarPassword ? '/cambiar-clave' : '/' });
    } catch (err) {
      setOpciones(null);
      if (err instanceof ErrorApi && err.code === 'USER_LOCKED') setError(t('login.errorBloqueado'));
      else if (err instanceof ErrorApi && err.code === 'COMPANY_INACTIVE')
        setError(t('login.errorEmpresaInactiva'));
      else if (err instanceof ErrorApi && err.code === 'INVALID_CREDENTIALS')
        setError(t('login.errorCredenciales'));
      else if (err instanceof ErrorApi && err.status === 429) setError(t('login.errorLimite'));
      else setError(t('login.errorGenerico'));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <main className="flex min-h-full items-center justify-center py-6">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void ingresar();
        }}
        className="tarjeta w-full max-w-md space-y-4 p-8"
      >
        <h1 className="text-[30px] font-light">
          {t('login.titulo')}
        </h1>

        <Campo
          etiqueta={t('login.usuario')}
          autoComplete="username"
          inputMode="text"
          value={username}
          onChange={(e) => {
            setUsername(e.target.value);
            setOpciones(null);
          }}
          required
        />

        <Campo
          etiqueta={t('login.clave')}
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setOpciones(null);
          }}
          required
        />

        {opciones ? (
          <div className="interno space-y-2 p-3">
            <p className="text-sm text-principal">{t('login.variasCuentas')}</p>
            {opciones.map((o) => (
              <Boton
                key={o.slug}
                type="button"
                variante="secundario"
                className="w-full"
                onClick={() => void ingresar(o.slug)}
              >
                {o.nombre}
              </Boton>
            ))}
          </div>
        ) : null}

        {error ? <Aviso>{error}</Aviso> : null}

        {!opciones ? (
          <Boton type="submit" className="w-full" disabled={enviando}>
            {enviando ? t('login.entrando') : t('login.entrar')}
          </Boton>
        ) : null}

        <Boton
          type="button"
          variante="texto"
          className="w-full"
          onClick={() => void navegar({ to: '/recuperar' })}
        >
          {t('login.olvide')}
        </Boton>
      </form>
    </main>
  );
}
